/**
 * Tax Table Admin UI
 *
 * Admin interface for managing tax tables in Wasabi storage.
 * FERP Architecture:
 * - Repository pattern (TaxTableRepository for all data access)
 * - Backend ID generation (Snowflake IDs only)
 * - Data-driven design (all tax data from Wasabi via GraphQL)
 * - Immutable audit trails (7-year retention with Object Lock)
 * - Compliance: IRS Publication 15-T federal tax brackets
 *
 * Issue #516 - Tax Table Pipeline
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Badge,
  Tabs,
  Box,
  Alert,
  LoadingOverlay,
  Button,
  Stack,
  Paper,
  Select,
  NumberInput,
  TextInput,
  Textarea,
  FileInput,
  Table,
  Modal,
  Switch,
  Divider,
  ActionIcon,
  Tooltip,
  Code,
} from '@mantine/core';
import {
  IconReceipt,
  IconUpload,
  IconCheck,
  IconAlertCircle,
  IconRefresh,
  IconTrash,
  IconHistory,
  IconDownload,
  IconFileSpreadsheet,
  IconPlus,
  IconBuildingBank,
  IconMap,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient, gql } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../security/useAuth';

// ============================================================================
// Types
// ============================================================================

export interface TaxBracket {
  minIncome: string;
  maxIncome: string | null;
  rate: string;
  baseTax: string;
}

export interface DeductionRules {
  standardDeduction: string;
  exemptionAmount: string;
  max401kContribution: string;
  maxHsaContribution: string;
  stateSpecificRules: string;
}

export interface TaxTable {
  tableId: string;
  tenantId: string;
  version: string;
  jurisdiction: string;
  taxYear: number;
  brackets: TaxBracket[];
  deductions: DeductionRules;
  socialSecurityRate: string;
  medicareRate: string;
  effectiveDate: string;
  expiresDate: string | null;
  lastUpdated: string;
}

export interface TaxTableVersion {
  version: string;
  uploadedAt: string;
  uploadedBy: string;
  source: string;
  notes: string | null;
  contentHash: string;
  isActive: boolean;
}

// ============================================================================
// GraphQL Queries and Mutations
// ============================================================================

const GET_TAX_TABLE = gql`
  query GetTaxTable($tenantId: String!, $jurisdiction: String!, $taxYear: Int!, $version: String) {
    getTaxTable(tenantId: $tenantId, jurisdiction: $jurisdiction, taxYear: $taxYear, version: $version) {
      taxTable {
        tableId
        tenantId
        version
        jurisdiction
        taxYear
        brackets {
          minIncome
          maxIncome
          rate
          baseTax
        }
        deductions {
          standardDeduction
          exemptionAmount
          max401kContribution
          maxHsaContribution
          stateSpecificRules
        }
        socialSecurityRate
        medicareRate
        effectiveDate
        expiresDate
        lastUpdated
      }
      source
      fetchDurationMs
    }
  }
`;

const LIST_TAX_TABLE_VERSIONS = gql`
  query ListTaxTableVersions($tenantId: String!, $taxYear: Int!, $jurisdiction: String!) {
    listTaxTableVersions(tenantId: $tenantId, taxYear: $taxYear, jurisdiction: $jurisdiction) {
      tenantId
      taxYear
      jurisdiction
      versions {
        version
        uploadedAt
        uploadedBy
        source
        notes
        contentHash
        isActive
      }
    }
  }
`;

const LIST_TAX_TABLE_JURISDICTIONS = gql`
  query ListTaxTableJurisdictions($tenantId: String!, $taxYear: Int!) {
    listTaxTableJurisdictions(tenantId: $tenantId, taxYear: $taxYear)
  }
`;

const UPLOAD_TAX_TABLE = gql`
  mutation UploadTaxTable($input: UploadTaxTableInput!, $uploadedBy: String!) {
    uploadTaxTable(input: $input, uploadedBy: $uploadedBy) {
      wasabiKey
      version
      uploadedAt
      retentionUntil
    }
  }
`;

const SET_ACTIVE_VERSION = gql`
  mutation SetActiveTaxTableVersion($tenantId: String!, $jurisdiction: String!, $taxYear: Int!, $version: String!) {
    setActiveTaxTableVersion(tenantId: $tenantId, jurisdiction: $jurisdiction, taxYear: $taxYear, version: $version)
  }
`;

const INVALIDATE_CACHE = gql`
  mutation InvalidateTaxTableCache($tenantId: String!, $jurisdiction: String, $taxYear: Int) {
    invalidateTaxTableCache(tenantId: $tenantId, jurisdiction: $jurisdiction, taxYear: $taxYear)
  }
`;

// ============================================================================
// Component Props
// ============================================================================

export interface TaxTableAdminProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId?: string;
  /** Current user ID for audit trail */
  userId?: string;
  /** Callback when tax table is uploaded */
  onUpload?: (version: string) => void;
}

// ============================================================================
// Federal Tax Brackets (2025 - IRS Publication 15-T)
// ============================================================================

const FEDERAL_2025_BRACKETS: TaxBracket[] = [
  { minIncome: '0', maxIncome: '11600', rate: '0.10', baseTax: '0' },
  { minIncome: '11600', maxIncome: '47150', rate: '0.12', baseTax: '1160' },
  { minIncome: '47150', maxIncome: '100525', rate: '0.22', baseTax: '5426' },
  { minIncome: '100525', maxIncome: '191950', rate: '0.24', baseTax: '17168.50' },
  { minIncome: '191950', maxIncome: '243725', rate: '0.32', baseTax: '39110.50' },
  { minIncome: '243725', maxIncome: '609350', rate: '0.35', baseTax: '55678.50' },
  { minIncome: '609350', maxIncome: null, rate: '0.37', baseTax: '183647.25' },
];

const FEDERAL_2025_DEDUCTIONS: DeductionRules = {
  standardDeduction: '14600',
  exemptionAmount: '0',
  max401kContribution: '23500',
  maxHsaContribution: '4300',
  stateSpecificRules: '{}',
};

// ============================================================================
// US States
// ============================================================================

const US_STATES = [
  { value: 'federal', label: 'Federal' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

// ============================================================================
// Component
// ============================================================================

export function TaxTableAdmin({
  tenantId: propTenantId,
  userId: propUserId,
  onUpload,
}: TaxTableAdminProps) {
  // =========================================================================
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS (Rules of Hooks)
  // =========================================================================

  // Apollo client
  const apolloClient = useApolloClient() as ApolloClient;

  // Get tenant and user from auth context (FERP pattern: no hardcoded values)
  const { tenant, user } = useAuth();

  // State
  const [activeTab, setActiveTab] = useState<string | null>('browse');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Browse state
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<string>('federal');
  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [versions, setVersions] = useState<TaxTableVersion[]>([]);
  const [currentTable, setCurrentTable] = useState<TaxTable | null>(null);
  const [fetchSource, setFetchSource] = useState<string>('');

  // Upload state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadJurisdiction, setUploadJurisdiction] = useState<string>('federal');
  const [uploadYear, setUploadYear] = useState<number>(new Date().getFullYear());
  const [uploadVersion, setUploadVersion] = useState<string>('');
  const [uploadSource, setUploadSource] = useState<string>('IRS Publication 15-T');
  const [uploadNotes, setUploadNotes] = useState<string>('');
  const [uploadSetActive, setUploadSetActive] = useState(true);
  const [uploadBrackets, setUploadBrackets] = useState<TaxBracket[]>(FEDERAL_2025_BRACKETS);
  const [uploadDeductions, setUploadDeductions] = useState<DeductionRules>(FEDERAL_2025_DEDUCTIONS);

  // CSV import state
  const [csvFile, setCsvFile] = useState<File | null>(null);

  // Resolve tenant and user IDs (props override auth context)
  const tenantId = propTenantId || tenant?.id;
  const userId = propUserId || user?.id;

  /**
   * Load available jurisdictions
   */
  const loadJurisdictions = useCallback(async () => {
    try {
      const result = await apolloClient.query<Record<string, any>>({
        query: LIST_TAX_TABLE_JURISDICTIONS,
        variables: { tenantId, taxYear: selectedYear },
        fetchPolicy: 'network-only',
      });
      setJurisdictions(result.data?.listTaxTableJurisdictions || []);
    } catch (err: any) {
      console.error('Failed to load jurisdictions:', err);
    }
  }, [apolloClient, tenantId, selectedYear]);

  /**
   * Load tax table versions
   */
  const loadVersions = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apolloClient.query<Record<string, any>>({
        query: LIST_TAX_TABLE_VERSIONS,
        variables: { tenantId, taxYear: selectedYear, jurisdiction: selectedJurisdiction },
        fetchPolicy: 'network-only',
      });
      setVersions(result.data?.listTaxTableVersions?.versions || []);
    } catch (err: any) {
      console.error('Failed to load versions:', err);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [apolloClient, tenantId, selectedYear, selectedJurisdiction]);

  /**
   * Load current tax table
   */
  const loadTaxTable = useCallback(async (version?: string) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apolloClient.query<Record<string, any>>({
        query: GET_TAX_TABLE,
        variables: {
          tenantId,
          jurisdiction: selectedJurisdiction,
          taxYear: selectedYear,
          version,
        },
        fetchPolicy: 'network-only',
      });
      setCurrentTable(result.data?.getTaxTable?.taxTable || null);
      setFetchSource(result.data?.getTaxTable?.source || 'Unknown');
    } catch (err: any) {
      console.error('Failed to load tax table:', err);
      setError(err.message || 'Failed to load tax table');
      setCurrentTable(null);
    } finally {
      setLoading(false);
    }
  }, [apolloClient, tenantId, selectedJurisdiction, selectedYear]);

  /**
   * Upload new tax table
   */
  const handleUpload = useCallback(async () => {
    try {
      setLoading(true);

      const effectiveDate = `${uploadYear}-01-01`;
      const tableId = `${uploadJurisdiction}-${uploadYear}-${uploadVersion}`;

      const result = await apolloClient.mutate<Record<string, any>>({
        mutation: UPLOAD_TAX_TABLE,
        variables: {
          input: {
            tenantId,
            tableId,
            version: uploadVersion,
            jurisdiction: uploadJurisdiction,
            taxYear: uploadYear,
            brackets: uploadBrackets,
            deductions: uploadDeductions,
            socialSecurityRate: '0.062',
            medicareRate: '0.0145',
            effectiveDate,
            expiresDate: null,
            source: uploadSource,
            notes: uploadNotes || null,
            setAsActive: uploadSetActive,
          },
          uploadedBy: userId,
        },
      });

      notifications.show({
        title: 'Success',
        message: `Tax table uploaded: ${result.data?.uploadTaxTable.version}`,
        color: 'green',
        icon: <IconCheck />,
      });

      setUploadModalOpen(false);
      onUpload?.(result.data?.uploadTaxTable.version);

      // Reload data
      await loadVersions();
      await loadTaxTable();
    } catch (err: any) {
      console.error('Failed to upload tax table:', err);
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to upload tax table',
        color: 'red',
        icon: <IconAlertCircle />,
      });
    } finally {
      setLoading(false);
    }
  }, [
    apolloClient,
    tenantId,
    userId,
    uploadJurisdiction,
    uploadYear,
    uploadVersion,
    uploadSource,
    uploadNotes,
    uploadSetActive,
    uploadBrackets,
    uploadDeductions,
    onUpload,
    loadVersions,
    loadTaxTable,
  ]);

  /**
   * Set active version
   */
  const handleSetActive = useCallback(async (version: string) => {
    try {
      setLoading(true);
      await apolloClient.mutate<Record<string, any>>({
        mutation: SET_ACTIVE_VERSION,
        variables: {
          tenantId,
          jurisdiction: selectedJurisdiction,
          taxYear: selectedYear,
          version,
        },
      });

      notifications.show({
        title: 'Success',
        message: `Version ${version} is now active`,
        color: 'green',
        icon: <IconCheck />,
      });

      await loadVersions();
      await loadTaxTable();
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to set active version',
        color: 'red',
        icon: <IconAlertCircle />,
      });
    } finally {
      setLoading(false);
    }
  }, [apolloClient, tenantId, selectedJurisdiction, selectedYear, loadVersions, loadTaxTable]);

  /**
   * Invalidate cache
   */
  const handleInvalidateCache = useCallback(async () => {
    try {
      await apolloClient.mutate<Record<string, any>>({
        mutation: INVALIDATE_CACHE,
        variables: {
          tenantId,
          jurisdiction: selectedJurisdiction,
          taxYear: selectedYear,
        },
      });

      notifications.show({
        title: 'Success',
        message: 'Cache invalidated',
        color: 'green',
        icon: <IconCheck />,
      });

      await loadTaxTable();
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to invalidate cache',
        color: 'red',
        icon: <IconAlertCircle />,
      });
    }
  }, [apolloClient, tenantId, selectedJurisdiction, selectedYear, loadTaxTable]);

  /**
   * Load federal 2025 defaults
   */
  const handleLoadFederalDefaults = useCallback(() => {
    setUploadJurisdiction('federal');
    setUploadYear(2025);
    setUploadVersion(`2025.${Date.now()}`);
    setUploadSource('IRS Publication 15-T');
    setUploadBrackets(FEDERAL_2025_BRACKETS);
    setUploadDeductions(FEDERAL_2025_DEDUCTIONS);
    setUploadNotes('Federal tax brackets from IRS Publication 15-T for tax year 2025');
  }, []);

  /**
   * Parse CSV file for state tax brackets
   */
  const handleCsvImport = useCallback(async () => {
    if (!csvFile) return;

    try {
      const text = await csvFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      const brackets: TaxBracket[] = [];

      // Skip header row
      for (let i = 1; i < lines.length; i++) {
        const parts = (lines[i] ?? '').split(',').map(p => p.trim());
        if (parts.length >= 4) {
          brackets.push({
            minIncome: parts[0] ?? '',
            maxIncome: parts[1] || null,
            rate: parts[2] ?? '',
            baseTax: parts[3] ?? '',
          });
        }
      }

      if (brackets.length > 0) {
        setUploadBrackets(brackets);
        notifications.show({
          title: 'Success',
          message: `Imported ${brackets.length} tax brackets from CSV`,
          color: 'green',
          icon: <IconCheck />,
        });
      }
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: 'Failed to parse CSV file',
        color: 'red',
        icon: <IconAlertCircle />,
      });
    }
  }, [csvFile]);

  // Load data on mount and when selection changes
  useEffect(() => {
    loadJurisdictions();
  }, [loadJurisdictions]);

  useEffect(() => {
    loadVersions();
    loadTaxTable();
  }, [loadVersions, loadTaxTable]);

  // =========================================================================
  // CONDITIONAL RETURNS (after all hooks are called)
  // =========================================================================

  // Validate tenant context is available
  if (!tenantId) {
    return (
      <Alert color="red" icon={<IconAlertCircle />}>
        Tenant context required. Please ensure you are authenticated.
      </Alert>
    );
  }

  return (
    <Container size="xl" py="xl">
      <LoadingOverlay visible={loading} />

      {/* Header */}
      <Group justify="space-between" mb="xl">
        <Box>
          <Title order={1}>
            <Group gap="xs">
              <IconReceipt size={32} />
              Tax Table Administration
            </Group>
          </Title>
          <Text c="dimmed" size="sm" mt="xs">
            Manage tax tables in Wasabi storage (7-year immutable retention)
          </Text>
        </Box>

        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={() => {
              loadVersions();
              loadTaxTable();
            }}
            variant="light"
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconUpload size={16} />}
            onClick={() => setUploadModalOpen(true)}
          >
            Upload Tax Table
          </Button>
        </Group>
      </Group>

      {/* Error alert */}
      {error && (
        <Alert icon={<IconAlertCircle />} title="Error" color="red" mb="md">
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="browse" leftSection={<IconBuildingBank size={16} />}>
            Browse Tax Tables
          </Tabs.Tab>
          <Tabs.Tab value="versions" leftSection={<IconHistory size={16} />}>
            Version History
          </Tabs.Tab>
          <Tabs.Tab value="jurisdictions" leftSection={<IconMap size={16} />}>
            Jurisdictions
          </Tabs.Tab>
        </Tabs.List>

        {/* Browse Tab */}
        <Tabs.Panel value="browse" pt="md">
          <Stack gap="md">
            {/* Selection controls */}
            <Paper p="md" withBorder>
              <Group>
                <NumberInput
                  label="Tax Year"
                  value={selectedYear}
                  onChange={(val) => setSelectedYear(val as number)}
                  min={2020}
                  max={2030}
                  w={120}
                />
                <Select
                  label="Jurisdiction"
                  value={selectedJurisdiction}
                  onChange={(val) => setSelectedJurisdiction(val || 'federal')}
                  data={US_STATES}
                  searchable
                  w={200}
                />
                <Button
                  mt="xl"
                  variant="light"
                  onClick={handleInvalidateCache}
                  leftSection={<IconRefresh size={16} />}
                >
                  Invalidate Cache
                </Button>
              </Group>
            </Paper>

            {/* Current tax table */}
            {currentTable ? (
              <Paper p="md" withBorder>
                <Group justify="space-between" mb="md">
                  <Box>
                    <Group gap="xs">
                      <Text fw={600} size="lg">
                        {currentTable.jurisdiction.toUpperCase()} Tax Table {currentTable.taxYear}
                      </Text>
                      <Badge color="blue">v{currentTable.version}</Badge>
                      <Badge color={fetchSource === 'ValkeyCache' ? 'green' : 'orange'}>
                        {fetchSource}
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed" mt="xs">
                      Effective: {currentTable.effectiveDate} | Last Updated: {currentTable.lastUpdated}
                    </Text>
                  </Box>
                  <Button
                    variant="light"
                    leftSection={<IconDownload size={16} />}
                    onClick={() => {
                      const json = JSON.stringify(currentTable, null, 2);
                      const blob = new Blob([json], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `tax-table-${currentTable.jurisdiction}-${currentTable.taxYear}.json`;
                      a.click();
                    }}
                  >
                    Export JSON
                  </Button>
                </Group>

                <Divider my="md" />

                {/* Tax brackets table */}
                <Text fw={500} mb="sm">Tax Brackets</Text>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Min Income</Table.Th>
                      <Table.Th>Max Income</Table.Th>
                      <Table.Th>Rate</Table.Th>
                      <Table.Th>Base Tax</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {currentTable.brackets.map((bracket, idx) => (
                      <Table.Tr key={idx}>
                        <Table.Td>${Number(bracket.minIncome).toLocaleString()}</Table.Td>
                        <Table.Td>
                          {bracket.maxIncome
                            ? `$${Number(bracket.maxIncome).toLocaleString()}`
                            : 'No limit'}
                        </Table.Td>
                        <Table.Td>{(Number(bracket.rate) * 100).toFixed(0)}%</Table.Td>
                        <Table.Td>${Number(bracket.baseTax).toLocaleString()}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>

                <Divider my="md" />

                {/* Deductions and rates */}
                <Group grow>
                  <Box>
                    <Text fw={500} mb="sm">Deduction Rules</Text>
                    <Stack gap="xs">
                      <Text size="sm">
                        Standard Deduction: ${Number(currentTable.deductions.standardDeduction).toLocaleString()}
                      </Text>
                      <Text size="sm">
                        Max 401(k): ${Number(currentTable.deductions.max401kContribution).toLocaleString()}
                      </Text>
                      <Text size="sm">
                        Max HSA: ${Number(currentTable.deductions.maxHsaContribution).toLocaleString()}
                      </Text>
                    </Stack>
                  </Box>
                  <Box>
                    <Text fw={500} mb="sm">FICA Rates</Text>
                    <Stack gap="xs">
                      <Text size="sm">
                        Social Security: {(Number(currentTable.socialSecurityRate) * 100).toFixed(2)}%
                      </Text>
                      <Text size="sm">
                        Medicare: {(Number(currentTable.medicareRate) * 100).toFixed(2)}%
                      </Text>
                    </Stack>
                  </Box>
                </Group>
              </Paper>
            ) : (
              <Paper p="xl" withBorder>
                <Text c="dimmed" ta="center">
                  No tax table found for {selectedJurisdiction.toUpperCase()} {selectedYear}
                </Text>
                <Button
                  mt="md"
                  mx="auto"
                  display="block"
                  onClick={() => {
                    setUploadJurisdiction(selectedJurisdiction);
                    setUploadYear(selectedYear);
                    setUploadModalOpen(true);
                  }}
                >
                  Upload Tax Table
                </Button>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Versions Tab */}
        <Tabs.Panel value="versions" pt="md">
          <Paper p="md" withBorder>
            <Group mb="md">
              <NumberInput
                label="Tax Year"
                value={selectedYear}
                onChange={(val) => setSelectedYear(val as number)}
                min={2020}
                max={2030}
                w={120}
              />
              <Select
                label="Jurisdiction"
                value={selectedJurisdiction}
                onChange={(val) => setSelectedJurisdiction(val || 'federal')}
                data={US_STATES}
                searchable
                w={200}
              />
            </Group>

            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>Uploaded</Table.Th>
                  <Table.Th>By</Table.Th>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {versions.map((version) => (
                  <Table.Tr key={version.version}>
                    <Table.Td>
                      <Code>{version.version}</Code>
                    </Table.Td>
                    <Table.Td>{new Date(version.uploadedAt).toLocaleString()}</Table.Td>
                    <Table.Td>{version.uploadedBy}</Table.Td>
                    <Table.Td>{version.source}</Table.Td>
                    <Table.Td>
                      {version.isActive ? (
                        <Badge color="green">Active</Badge>
                      ) : (
                        <Badge color="gray">Inactive</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="View this version">
                          <ActionIcon
                            variant="light"
                            onClick={() => loadTaxTable(version.version)}
                          >
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {!version.isActive && (
                          <Tooltip label="Set as active">
                            <ActionIcon
                              variant="light"
                              color="green"
                              onClick={() => handleSetActive(version.version)}
                            >
                              <IconCheck size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {versions.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="md">
                        No versions found for {selectedJurisdiction.toUpperCase()} {selectedYear}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        </Tabs.Panel>

        {/* Jurisdictions Tab */}
        <Tabs.Panel value="jurisdictions" pt="md">
          <Paper p="md" withBorder>
            <Group mb="md">
              <NumberInput
                label="Tax Year"
                value={selectedYear}
                onChange={(val) => setSelectedYear(val as number)}
                min={2020}
                max={2030}
                w={120}
              />
            </Group>

            <Text fw={500} mb="md">
              Configured Jurisdictions for {selectedYear}
            </Text>

            {jurisdictions.length > 0 ? (
              <Group gap="xs">
                {jurisdictions.map((j) => (
                  <Badge
                    key={j}
                    size="lg"
                    variant="light"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedJurisdiction(j);
                      setActiveTab('browse');
                    }}
                  >
                    {j.toUpperCase()}
                  </Badge>
                ))}
              </Group>
            ) : (
              <Text c="dimmed">
                No jurisdictions configured for {selectedYear}
              </Text>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Upload Modal */}
      <Modal
        opened={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        title="Upload Tax Table"
        size="lg"
      >
        <Stack gap="md">
          {/* Quick actions */}
          <Group>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconBuildingBank size={14} />}
              onClick={handleLoadFederalDefaults}
            >
              Load Federal 2025 Defaults
            </Button>
          </Group>

          <Divider />

          {/* Basic info */}
          <Group grow>
            <Select
              label="Jurisdiction"
              value={uploadJurisdiction}
              onChange={(val) => setUploadJurisdiction(val || 'federal')}
              data={US_STATES}
              required
            />
            <NumberInput
              label="Tax Year"
              value={uploadYear}
              onChange={(val) => setUploadYear(val as number)}
              min={2020}
              max={2030}
              required
            />
          </Group>

          <Group grow>
            <TextInput
              label="Version"
              value={uploadVersion}
              onChange={(e) => setUploadVersion(e.currentTarget.value)}
              placeholder="e.g., 2025.1"
              required
            />
            <TextInput
              label="Source"
              value={uploadSource}
              onChange={(e) => setUploadSource(e.currentTarget.value)}
              placeholder="e.g., IRS Publication 15-T"
            />
          </Group>

          <Textarea
            label="Notes"
            value={uploadNotes}
            onChange={(e) => setUploadNotes(e.currentTarget.value)}
            placeholder="Optional notes about this version"
          />

          <Switch
            label="Set as active version"
            checked={uploadSetActive}
            onChange={(e) => setUploadSetActive(e.currentTarget.checked)}
          />

          <Divider label="Tax Brackets" />

          {/* CSV Import */}
          <Group>
            <FileInput
              label="Import from CSV"
              placeholder="Select CSV file"
              accept=".csv"
              value={csvFile}
              onChange={setCsvFile}
              leftSection={<IconFileSpreadsheet size={16} />}
              style={{ flex: 1 }}
            />
            <Button
              mt="xl"
              onClick={handleCsvImport}
              disabled={!csvFile}
            >
              Import
            </Button>
          </Group>

          <Text size="xs" c="dimmed">
            CSV format: min_income, max_income, rate, base_tax (one bracket per line)
          </Text>

          {/* Brackets preview */}
          <Paper p="sm" withBorder>
            <Text size="sm" fw={500} mb="xs">
              {uploadBrackets.length} brackets configured
            </Text>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Min</Table.Th>
                  <Table.Th>Max</Table.Th>
                  <Table.Th>Rate</Table.Th>
                  <Table.Th>Base</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {uploadBrackets.slice(0, 5).map((b, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>${Number(b.minIncome).toLocaleString()}</Table.Td>
                    <Table.Td>{b.maxIncome ? `$${Number(b.maxIncome).toLocaleString()}` : '-'}</Table.Td>
                    <Table.Td>{(Number(b.rate) * 100).toFixed(0)}%</Table.Td>
                    <Table.Td>${Number(b.baseTax).toLocaleString()}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {uploadBrackets.length > 5 && (
              <Text size="xs" c="dimmed" mt="xs">
                ...and {uploadBrackets.length - 5} more brackets
              </Text>
            )}
          </Paper>

          <Divider />

          {/* Actions */}
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setUploadModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!uploadVersion || !uploadJurisdiction || uploadBrackets.length === 0}
            >
              Upload
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
